/**
 * The live sheet grid, pure parts: its size and which rows and columns are
 * on screen, the selection and how keys move it, the clipboard (TSV blocks
 * in and out), and where everyone else's selected cell is, read from the
 * document's awareness.
 *
 * The cells themselves are the shared model (`@amoreg/shared/spaceSheet`).
 *
 * @module components/spaces/sheet/sheetGrid.logic
 */
import {
  SHEET_LIMITS,
  type SheetCell,
  type SheetCellChange,
  cellFromInput,
  cellInputText,
  cellKey,
  parseCellKey,
  parseClipboardTable,
  toClipboardTable,
} from "@amoreg/shared/spaceSheet";

export const ROW_HEIGHT = 28;
export const HEADER_HEIGHT = 28;
export const ROW_HEADER_WIDTH = 52;
export const DEFAULT_COL_WIDTH = 104;
const MIN_COL_WIDTH = 40;
const MAX_COL_WIDTH = 480;
/** The largest block one paste writes: one push stays well under the update limit. */
export const MAX_PASTE_CELLS = 10_000;
/** The largest block one copy writes out; a block over a sparse sheet can name billions of cells. */
export const MAX_COPY_CELLS = SHEET_LIMITS.cells;
/**
 * The most cell content one write sends in a single update: half the
 * server's 1 MiB update limit, leaving room for Yjs's own encoding. A cell
 * count alone does not bound it: 100 cells of 20,000 characters are 2 MB.
 */
export const MAX_WRITE_BATCH_BYTES = 512 * 1024;
/** What each change costs beyond its text: the key, the cell's fields, Yjs ids. */
const CHANGE_OVERHEAD_BYTES = 64;

const utf8Length = (text: string): number => new TextEncoder().encode(text).byteLength;

/** Roughly what one change adds to an update, erring high. */
export function cellChangeBytes(change: SheetCellChange): number {
  if (change.cell === null) return CHANGE_OVERHEAD_BYTES;
  return CHANGE_OVERHEAD_BYTES + utf8Length(JSON.stringify(change.cell));
}

/**
 * `changes` split into writes that each stay under
 * {@link MAX_WRITE_BATCH_BYTES}, in order, so a large paste or clear goes
 * out as several updates the server accepts instead of one it refuses (a
 * refused update would leave cells only on this screen). One cell is never
 * split; Excel's text limit keeps a single cell far under the budget.
 */
export function writeBatches(
  changes: ReadonlyArray<SheetCellChange>,
  maxBytes: number = MAX_WRITE_BATCH_BYTES,
): SheetCellChange[][] {
  const batches: SheetCellChange[][] = [];
  let current: SheetCellChange[] = [];
  let bytes = 0;
  for (const change of changes) {
    const size = cellChangeBytes(change);
    if (current.length > 0 && bytes + size > maxBytes) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(change);
    bytes += size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

/** An Excel column width (character units) in pixels; the default without one. */
export function columnWidthPx(excelWidth: number | undefined): number {
  if (excelWidth === undefined || !Number.isFinite(excelWidth) || excelWidth <= 0) return DEFAULT_COL_WIDTH;
  return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, Math.round(excelWidth * 7 + 5)));
}

/** Rows and columns the grid shows: every filled cell, the file's extent, and room to grow. */
export function gridSize(input: {
  readonly extentRows: number;
  readonly extentCols: number;
  readonly rowCount: number;
  readonly colCount: number;
}): { readonly rows: number; readonly cols: number } {
  return {
    rows: Math.min(SHEET_LIMITS.rows, Math.max(input.extentRows + 30, input.rowCount, 60)),
    cols: Math.min(SHEET_LIMITS.columns, Math.max(input.extentCols + 4, input.colCount, 12)),
  };
}

/** Left edge of every column, and the total width at the end (`cols + 1` entries). */
export function columnOffsets(cols: number, widthPx: (col: number) => number): number[] {
  const offsets = Array.from<number>({ length: cols + 1 });
  offsets[0] = 0;
  for (let col = 0; col < cols; col += 1) offsets[col + 1] = offsets[col]! + widthPx(col);
  return offsets;
}

/** The index of the last offset at or before `position` (binary search). */
function indexAt(offsets: ReadonlyArray<number>, position: number): number {
  let low = 0;
  let high = offsets.length - 2;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (offsets[middle]! <= position) low = middle;
    else high = middle - 1;
  }
  return Math.max(0, low);
}

/** The columns in [scrollLeft, scrollLeft + width], plus `overscan` either side. */
export function visibleColumns(
  offsets: ReadonlyArray<number>,
  scrollLeft: number,
  width: number,
  overscan = 2,
): { readonly first: number; readonly last: number } {
  const cols = offsets.length - 1;
  if (cols <= 0) return { first: 0, last: -1 };
  const first = Math.max(0, indexAt(offsets, scrollLeft) - overscan);
  const last = Math.min(cols - 1, indexAt(offsets, scrollLeft + Math.max(0, width)) + overscan);
  return { first, last };
}

/**
 * The cell under a point given in the scroller's viewport, or null on the
 * pinned column letters or row numbers (they stay put while the cells
 * scroll, so the point is tested before the scroll is added) and past the
 * last row.
 */
export function cellAtPoint(
  point: { readonly x: number; readonly y: number; readonly scrollLeft: number; readonly scrollTop: number },
  offsets: ReadonlyArray<number>,
  rows: number,
): CellPosition | null {
  if (point.x < ROW_HEADER_WIDTH || point.y < HEADER_HEIGHT || offsets.length < 2) return null;
  const row = Math.floor((point.y - HEADER_HEIGHT + point.scrollTop) / ROW_HEIGHT);
  const col = indexAt(offsets, point.x - ROW_HEADER_WIDTH + point.scrollLeft);
  return row < rows ? { row, col } : null;
}

/** The rows in [scrollTop, scrollTop + height], plus `overscan` either side. */
export function visibleRows(
  scrollTop: number,
  height: number,
  rows: number,
  overscan = 6,
): { readonly first: number; readonly last: number } {
  if (rows <= 0) return { first: 0, last: -1 };
  const first = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - overscan);
  const last = Math.min(rows - 1, Math.ceil((scrollTop + Math.max(0, height)) / ROW_HEIGHT) + overscan);
  return { first, last };
}

/* ------------------------------------------------------------------ */
/* Selection                                                           */
/* ------------------------------------------------------------------ */

export interface CellPosition {
  readonly row: number;
  readonly col: number;
}

/** The anchor is where the selection started; the focus moves with the keys. */
export interface SheetSelection {
  readonly anchor: CellPosition;
  readonly focus: CellPosition;
}

export interface SelectionBounds {
  readonly top: number;
  readonly left: number;
  readonly bottom: number;
  readonly right: number;
}

export const singleCell = (position: CellPosition): SheetSelection => ({ anchor: position, focus: position });

export function selectionBounds(selection: SheetSelection): SelectionBounds {
  return {
    top: Math.min(selection.anchor.row, selection.focus.row),
    left: Math.min(selection.anchor.col, selection.focus.col),
    bottom: Math.max(selection.anchor.row, selection.focus.row),
    right: Math.max(selection.anchor.col, selection.focus.col),
  };
}

export const isSingleCell = (selection: SheetSelection): boolean =>
  selection.anchor.row === selection.focus.row && selection.anchor.col === selection.focus.col;

export type MoveKey = "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight" | "Enter" | "Tab";

const clamp = (value: number, max: number) => Math.min(Math.max(0, value), Math.max(0, max - 1));

/**
 * The selection after a key: arrows move one cell (with Shift they extend
 * the block); Enter moves down and Tab right (Shift: up, left), always to
 * one cell. Moves stop at the grid's edge.
 */
export function moveSelection(
  selection: SheetSelection,
  key: MoveKey,
  options: { readonly shift: boolean; readonly rows: number; readonly cols: number },
): SheetSelection {
  const step = (position: CellPosition, rows: number, cols: number): CellPosition => ({
    row: clamp(position.row + rows, options.rows),
    col: clamp(position.col + cols, options.cols),
  });
  const delta: Record<MoveKey, readonly [number, number]> = {
    ArrowUp: [-1, 0],
    ArrowDown: [1, 0],
    ArrowLeft: [0, -1],
    ArrowRight: [0, 1],
    Enter: options.shift ? [-1, 0] : [1, 0],
    Tab: options.shift ? [0, -1] : [0, 1],
  };
  const [rows, cols] = delta[key];
  if (options.shift && key.startsWith("Arrow")) {
    return { anchor: selection.anchor, focus: step(selection.focus, rows, cols) };
  }
  // A plain move, Enter and Tab all leave one cell selected, next to the focus.
  return singleCell(step(selection.focus, rows, cols));
}

/* ------------------------------------------------------------------ */
/* Clipboard                                                           */
/* ------------------------------------------------------------------ */

const blockCells = (bounds: SelectionBounds): number =>
  (bounds.bottom - bounds.top + 1) * (bounds.right - bounds.left + 1);

/**
 * The selected block as TSV: formulas with their "=", numbers unformatted,
 * so a paste reads back the same. Null when the block is larger than
 * {@link MAX_COPY_CELLS}.
 */
export function copySelection(
  bounds: SelectionBounds,
  read: (row: number, col: number) => SheetCell | undefined,
): string | null {
  if (blockCells(bounds) > MAX_COPY_CELLS) return null;
  const rows: string[][] = [];
  for (let row = bounds.top; row <= bounds.bottom; row += 1) {
    const line: string[] = [];
    for (let col = bounds.left; col <= bounds.right; col += 1) line.push(cellInputText(read(row, col)));
    rows.push(line);
  }
  return toClipboardTable(rows);
}

/**
 * The cell changes of pasting `text` with its top-left at `at`: each pasted
 * cell typed in as a person would (numbers become numbers, "=…" a formula,
 * empty clears). Cells past the grid's limits are dropped. Null when the
 * block is larger than {@link MAX_PASTE_CELLS}.
 */
export function pasteChanges(
  text: string,
  at: CellPosition,
  read: (row: number, col: number) => SheetCell | undefined,
): SheetCellChange[] | null {
  const block = parseClipboardTable(text);
  const cells = block.reduce((total, row) => total + row.length, 0);
  if (cells > MAX_PASTE_CELLS) return null;
  const changes: SheetCellChange[] = [];
  block.forEach((line, rowOffset) => {
    line.forEach((value, colOffset) => {
      const row = at.row + rowOffset;
      const col = at.col + colOffset;
      if (row >= SHEET_LIMITS.rows || col >= SHEET_LIMITS.columns) return;
      changes.push({ row, col, cell: cellFromInput(value, read(row, col)) });
    });
  });
  return changes;
}

/** The block a paste selects: from where it landed to its lowest row and widest column. */
export function pastedSelection(at: CellPosition, changes: ReadonlyArray<SheetCellChange>): SheetSelection {
  let row = at.row;
  let col = at.col;
  for (const change of changes) {
    row = Math.max(row, change.row);
    col = Math.max(col, change.col);
  }
  return { anchor: at, focus: { row, col } };
}

/**
 * Clearing the selected block: a change for each of its filled cells only,
 * in row order. It walks whichever is smaller, the block or the sheet's
 * filled cells, so a block over a sparse sheet stays as cheap as the cells
 * it holds.
 */
export function clearChanges(
  bounds: SelectionBounds,
  filled: { readonly size: number; has(key: string): boolean; keys(): Iterable<string> } | null,
): SheetCellChange[] {
  if (filled === null || filled.size === 0) return [];
  const changes: SheetCellChange[] = [];
  if (blockCells(bounds) <= filled.size) {
    for (let row = bounds.top; row <= bounds.bottom; row += 1) {
      for (let col = bounds.left; col <= bounds.right; col += 1) {
        if (filled.has(cellKey(row, col))) changes.push({ row, col, cell: null });
      }
    }
    return changes;
  }
  for (const key of filled.keys()) {
    const at = parseCellKey(key);
    if (at === null || at.row < bounds.top || at.row > bounds.bottom) continue;
    if (at.col < bounds.left || at.col > bounds.right) continue;
    changes.push({ row: at.row, col: at.col, cell: null });
  }
  return changes.toSorted((left, right) => left.row - right.row || left.col - right.col);
}

/* ------------------------------------------------------------------ */
/* Presence                                                            */
/* ------------------------------------------------------------------ */

/** The awareness field a browser writes its selected cell into. */
export const SHEET_SELECTION_FIELD = "sheetSelection";

export interface SheetSelectionState {
  readonly sheetId: string;
  readonly row: number;
  readonly col: number;
}

export interface SheetCursor extends SheetSelectionState {
  /** One per browser: the same person in two tabs shows two cursors. */
  readonly key: string;
  readonly name: string;
  readonly color: string;
}

const COLOR = /^#[0-9a-f]{3,8}$/i;
const FALLBACK_COLOR = "#64748b";

const isPosition = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

/** Everyone else's selected cell, by sheet. The server stamps the names and colours. */
export function sheetCursors(
  states: ReadonlyMap<number, Record<string, unknown>>,
  ownClientId: number,
): SheetCursor[] {
  const cursors: SheetCursor[] = [];
  for (const [clientId, state] of states) {
    if (clientId === ownClientId) continue;
    const selection = state[SHEET_SELECTION_FIELD] as Record<string, unknown> | null | undefined;
    const user = state.user as Record<string, unknown> | null | undefined;
    if (!selection || typeof selection !== "object" || !user || typeof user !== "object") continue;
    const { sheetId, row, col } = selection;
    if (typeof sheetId !== "string" || !isPosition(row) || !isPosition(col)) continue;
    const name = typeof user.name === "string" && user.name.trim() ? user.name.trim() : "Someone";
    const color = typeof user.color === "string" && COLOR.test(user.color) ? user.color : FALLBACK_COLOR;
    cursors.push({ key: `client:${clientId}`, sheetId, row, col, name, color });
  }
  return cursors.toSorted((left, right) => left.name.localeCompare(right.name) || left.key.localeCompare(right.key));
}
